import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFilters } from '../hooks/useFilters';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const STATUT_COLORS = { Actif: '#22c55e', Expiré: '#ef4444', Résilié: '#94a3b8', 'En attente': '#f59e0b' };
const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>{text}</span>
);
const inputStyle = { padding: '8px 12px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' };

function ContratPanel({ contratId, onClose, onDelete }) {
  const fetchWithAuth = useAuthFetch();
  const [contrat, setContrat] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWithAuth(`/api/contrats/${contratId}`).then(r => r.json()).then(d => { setContrat(d); setForm(d); });
  }, [contratId]);

  const save = async () => {
    setSaving(true);
    const res = await fetchWithAuth(`/api/contrats/${contratId}`, { method: 'PUT', body: JSON.stringify(form) });
    const updated = await res.json(); setContrat(updated); setEditing(false); setSaving(false);
  };
  const del = async () => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    await fetchWithAuth(`/api/contrats/${contratId}`, { method: 'DELETE' });
    onClose(); onDelete?.();
  };

  const is = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#0f1117', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  if (!contrat) return null;

  const daysLeft = contrat.date_fin ? Math.ceil((new Date(contrat.date_fin) - new Date()) / 86400000) : null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      <div className="side-panel" style={{ width: 400 }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #2a2d3e', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0', marginBottom: 6 }}>{contrat.titre}</div>
            <Badge text={contrat.statut} color={STATUT_COLORS[contrat.statut] || '#64748b'} />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {daysLeft !== null && daysLeft <= 30 && contrat.statut === 'Actif' && (
            <div style={{ background: daysLeft <= 0 ? '#ef444420' : '#f59e0b20', border: `1px solid ${daysLeft <= 0 ? '#ef444440' : '#f59e0b40'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: daysLeft <= 0 ? '#ef4444' : '#f59e0b' }}>
              ⚠️ {daysLeft <= 0 ? 'Contrat expiré !' : `Expire dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`}
            </div>
          )}
          {!editing ? (
            <>
              <div style={{ background: '#41B07315', border: '1px solid #41B07330', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#94a3b8' }}>🏢 <span style={{ color: '#e2e8f0' }}>{contrat.client_nom}</span></div>
              {[['Type', contrat.type], ['Montant HT', fmt(contrat.montant_ht)], ['TVA', `${contrat.taux_tva}%`], ['TTC', fmt(contrat.montant_ht * (1 + contrat.taux_tva / 100))], ['Début', fmtDate(contrat.date_debut)], ['Fin', fmtDate(contrat.date_fin)], ['Renouvellement auto', contrat.renouvellement_auto ? 'Oui' : 'Non']].map(([l, v]) => (
                <div key={l} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2, textTransform: 'uppercase', fontWeight: 500 }}>{l}</div>
                  <div style={{ fontSize: 13, color: '#e2e8f0' }}>{v || '—'}</div>
                </div>
              ))}
              {contrat.notes && <div style={{ marginBottom: 12 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 2, textTransform: 'uppercase' }}>Notes</div><div style={{ fontSize: 13, color: '#e2e8f0' }}>{contrat.notes}</div></div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => setEditing(true)} style={{ padding: '8px 16px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>✏️ Modifier</button>
                <button onClick={del} style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid #ef444440', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>🗑 Supprimer</button>
              </div>
            </>
          ) : (
            <div>
              {[['titre', 'Titre'], ['type', 'Type', ['Maintenance Essentiel', 'Maintenance Évolutions', 'Forfait', 'Régie', 'Autre']], ['statut', 'Statut', ['Actif', 'Expiré', 'Résilié', 'En attente']], ['montant_ht', 'Montant HT'], ['taux_tva', 'TVA %'], ['date_debut', 'Date début (AAAA-MM-JJ)'], ['date_fin', 'Date fin (AAAA-MM-JJ)']].map(([k, l, opts]) => (
                <div key={k} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>{l}</label>
                  {opts ? <select value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={is}>{opts.map(o => <option key={o}>{o}</option>)}</select>
                    : <input value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={is} />}
                </div>
              ))}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Notes</label>
                <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...is, height: 70, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>{saving ? '...' : '💾 Enregistrer'}</button>
                <button onClick={() => { setEditing(false); setForm(contrat); }} style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function ContratsPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const { search, setSearch, page, setPage, limit, setLimit, sortField, sortDir, toggleSort, filters, setFilter, buildQuery } = useFilters({ sortField: 'titre' });
  const [contrats, setContrats] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(location.state?.contratId || null);
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [newContrat, setNewContrat] = useState({ client_id: '', titre: '', type: 'Maintenance Essentiel', montant_ht: '', taux_tva: 20, date_debut: '', date_fin: '', statut: 'Actif', renouvellement_auto: 0 });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => { fetchWithAuth('/api/clients?limit=999').then(r => r.json()).then(d => setClients(d.data || [])); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, all] = await Promise.all([
        fetchWithAuth(`/api/contrats?${buildQuery()}`).then(r => r.json()),
        fetchWithAuth('/api/contrats?limit=999').then(r => r.json()),
      ]);
      setContrats(res.data || []); setTotal(res.total || 0);
      const counts = { total: 0, Actif: 0, Expiré: 0, expiring30: 0 };
      const now = new Date();
      (all.data || []).forEach(c => {
        counts.total++;
        if (c.statut === 'Actif') counts.Actif++;
        if (c.statut === 'Expiré') counts.Expiré++;
        if (c.statut === 'Actif' && c.date_fin) {
          const days = Math.ceil((new Date(c.date_fin) - now) / 86400000);
          if (days >= 0 && days <= 30) counts.expiring30++;
        }
      });
      setKpis(counts);
    } finally { setLoading(false); }
  }, [search, page, limit, sortField, sortDir, JSON.stringify(filters)]);

  useEffect(() => { load(); }, [load]);

  const createContrat = async () => {
    const errors = {};
    if (!newContrat.titre.trim()) errors.titre = true;
    if (!newContrat.client_id) errors.client_id = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    await fetchWithAuth('/api/contrats', { method: 'POST', body: JSON.stringify(newContrat) });
    setShowModal(false); load();
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
  const totalPages = Math.ceil(total / limit);
  const is = { padding: '8px 10px', borderRadius: 6, background: '#0f1117', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Contrats</h1>
        <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouveau contrat</button>
      </div>
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total" value={kpis.total || 0} color="#41B073" icon="📋" />
        <KPICard title="Actifs" value={kpis.Actif || 0} color="#22c55e" onClick={() => setFilter('statut', 'Actif')} />
        <KPICard title="Expirés" value={kpis.Expiré || 0} color="#ef4444" onClick={() => setFilter('statut', 'Expiré')} />
        <KPICard title="Expirent dans 30j" value={kpis.expiring30 || 0} color="#f59e0b" icon="⚠️" alert={kpis.expiring30 > 0} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...inputStyle, minWidth: 200 }} />
        <select value={filters.statut || ''} onChange={e => setFilter('statut', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">Tous statuts</option>
          {['Actif', 'Expiré', 'Résilié', 'En attente'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">Tous types</option>
          {['Maintenance Essentiel', 'Maintenance Évolutions', 'Forfait', 'Régie', 'Autre'].map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>
      <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
              <SortTh field="titre">Titre</SortTh>
              <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Client</th>
              <SortTh field="type">Type</SortTh>
              <SortTh field="montant_ht">Montant HT</SortTh>
              <SortTh field="date_fin">Fin</SortTh>
              <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Statut</th>
              <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Reste</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Chargement...</td></tr>
              : contrats.length === 0 ? <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucun contrat</td></tr>
              : contrats.map((c, i) => {
                const daysLeft = c.date_fin ? Math.ceil((new Date(c.date_fin) - new Date()) / 86400000) : null;
                const daysColor = daysLeft === null ? '#64748b' : daysLeft < 0 ? '#ef4444' : daysLeft <= 30 ? '#f59e0b' : daysLeft <= 90 ? '#3b82f6' : '#22c55e';
                return (
                  <tr key={c.id} onClick={() => setSelectedId(c.id)} style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{c.titre}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.client_nom}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.type}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(c.montant_ht)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(c.date_fin)}</td>
                    <td style={{ padding: '11px 14px' }}><Badge text={c.statut} color={STATUT_COLORS[c.statut] || '#64748b'} /></td>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: daysColor, fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>
                      {daysLeft === null ? '—' : daysLeft < 0 ? `${Math.abs(daysLeft)}j dépassé` : `${daysLeft}j`}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a2d3e' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>{total} contrat{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page === 1 ? '#2a2d3e' : '#94a3b8', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: '#94a3b8' }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page >= totalPages ? '#2a2d3e' : '#94a3b8', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>
      {selectedId && <ContratPanel contratId={selectedId} onClose={() => setSelectedId(null)} onDelete={load} />}
      {showModal && (
        <>
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div className="modal-scroll center-modal" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 460, background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 14, padding: 28, zIndex: 201, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>Nouveau contrat</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Client *</label>
              <select value={newContrat.client_id} onChange={e => setNewContrat(f => ({ ...f, client_id: e.target.value }))} style={{ ...is, borderColor: formErrors.client_id ? '#ef4444' : '#2a2d3e', cursor: 'pointer' }}>
                <option value="">— Choisir —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            {[['titre', 'Titre *'], ['montant_ht', 'Montant HT'], ['taux_tva', 'TVA %'], ['date_debut', 'Date début (AAAA-MM-JJ)'], ['date_fin', 'Date fin (AAAA-MM-JJ)']].map(([k, l]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <input value={newContrat[k] || ''} onChange={e => setNewContrat(f => ({ ...f, [k]: e.target.value }))} style={{ ...is, borderColor: formErrors[k] ? '#ef4444' : '#2a2d3e' }} />
              </div>
            ))}
            {[['type', 'Type', ['Maintenance Essentiel', 'Maintenance Évolutions', 'Forfait', 'Régie', 'Autre']], ['statut', 'Statut', ['Actif', 'Expiré', 'Résilié', 'En attente']]].map(([k, l, opts]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <select value={newContrat[k]} onChange={e => setNewContrat(f => ({ ...f, [k]: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>{opts.map(o => <option key={o}>{o}</option>)}</select>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={createContrat} style={{ flex: 1, padding: '10px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'inherit' }}>Créer</button>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
